import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Eye, GitCompareArrows, Lock } from "lucide-react";
import { fmtCurrencyExact, fmtDateTime } from "@/lib/constants";

const fmt = (v) => (v == null ? "—" : fmtCurrencyExact(v));

function labelBadge(label) {
  if (label === "accepted") return <Badge variant="outline" className="rounded-sm text-[10px] border-emerald-300 bg-emerald-50 text-emerald-800">✓ ACCEPTED</Badge>;
  return <Badge variant="outline" className="rounded-sm text-[10px] border-sky-300 bg-sky-50 text-sky-800">SENT</Badge>;
}

export function CompareRevisionsDialog({ quoteId, rev, open, onOpenChange }) {
  const { data, isLoading } = useQuery({
    queryKey: ["quote-rev-compare", quoteId, rev],
    queryFn: async () => (await api.get(`/quotes/${quoteId}/revisions-compare?a=${rev}&b=current`)).data,
    enabled: open && !!rev,
  });
  const rowCls = { added: "bg-emerald-50", removed: "bg-rose-50", changed: "bg-amber-50", same: "" };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="compare-revisions-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Compare — Rev {rev} vs current{data ? ` (Rev ${data.new_revision})` : ""}</DialogTitle>
        </DialogHeader>
        {isLoading && <div className="py-8 text-center"><Loader2 className="animate-spin inline text-slate-400" /></div>}
        {data && (
          <div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b-2 border-slate-200">
                  <th className="py-1.5 pr-2">Line item</th>
                  <th className="py-1.5 text-right">Rev {rev}</th>
                  <th className="py-1.5 text-right">Current</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={i} className={`border-b border-slate-100 ${rowCls[r.kind] || ""}`} data-testid={`compare-row-${r.kind}`}>
                    <td className={`py-1.5 pr-2 ${r.kind === "removed" ? "line-through text-rose-700" : ""}`}>
                      {r.title}
                      {r.kind === "added" && <Badge variant="outline" className="ml-1.5 rounded-sm text-[9px] border-emerald-300 text-emerald-700">ADDED</Badge>}
                      {r.kind === "changed" && <Badge variant="outline" className="ml-1.5 rounded-sm text-[9px] border-amber-300 text-amber-700">CHANGED</Badge>}
                      {r.kind === "removed" && <Badge variant="outline" className="ml-1.5 rounded-sm text-[9px] border-rose-300 text-rose-700">REMOVED</Badge>}
                    </td>
                    <td className="py-1.5 text-right font-mono">{fmt(r.old_sale ?? r.old_cost)}</td>
                    <td className="py-1.5 text-right font-mono">{fmt(r.new_sale ?? r.new_cost)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-slate-900 font-bold">
                  <td className="py-2">Quote total (sale)</td>
                  <td className="py-2 text-right font-mono">{fmt(data.old_total)}</td>
                  <td className="py-2 text-right font-mono">{fmt(data.new_total)}</td>
                </tr>
              </tbody>
            </table>
            <div className="mt-1 text-[10px] text-slate-400">Line figures shown at client sale value (cost × GP uplift); extra line-items/adjustments mean lines may not sum exactly to the quote total.</div>
            <div className={`mt-2 text-right text-sm font-mono font-bold ${data.delta > 0 ? "text-emerald-700" : data.delta < 0 ? "text-rose-700" : "text-slate-500"}`} data-testid="compare-delta">
              {data.delta > 0 ? "+" : ""}{fmtCurrencyExact(data.delta)} vs Rev {rev}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function RevisionHistorySheet({ quoteId, open, onOpenChange, currentRevision, currentStatus, snapshotRev }) {
  const [compareRev, setCompareRev] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["quote-revisions", quoteId],
    queryFn: async () => (await api.get(`/quotes/${quoteId}/revisions`)).data,
    enabled: open,
  });
  const revs = data?.revisions || [];
  const curHasSnapshot = revs.some((r) => r.revision === currentRevision);
  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="revision-history-sheet">
          <SheetHeader>
            <SheetTitle className="font-display">Revision history</SheetTitle>
          </SheetHeader>
          <div className="text-[11px] text-slate-500 mt-1 mb-3 flex items-center gap-1.5">
            <Lock size={11} /> A snapshot is frozen every time a revision is sent or accepted — nothing is overwritten.
          </div>
          {isLoading && <div className="py-8 text-center"><Loader2 className="animate-spin inline text-slate-400" /></div>}
          <div className="space-y-2">
            {/* Current working revision (no snapshot yet = draft/pending) */}
            {!curHasSnapshot && (
              <div className="border-2 border-cyan-500 bg-cyan-50/50 rounded-sm p-3" data-testid="revision-row-current">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-sm">Rev {currentRevision}
                    <Badge variant="outline" className="ml-2 rounded-sm text-[10px] border-cyan-300 bg-cyan-50 text-cyan-800">CURRENT — {String(currentStatus || "draft").toUpperCase()}</Badge>
                  </div>
                  <span className="font-mono text-sm font-bold">{fmt(data?.current?.sale_price)}</span>
                </div>
              </div>
            )}
            {revs.map((r) => {
              const isCurrent = r.revision === currentRevision && !snapshotRev;
              return (
                <div key={r.revision}
                  className={`rounded-sm p-3 border ${isCurrent ? "border-2 border-cyan-500 bg-cyan-50/40" : r.label === "accepted" ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200"}`}
                  data-testid={`revision-row-${r.revision}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-sm">Rev {r.revision}
                      <span className="ml-2">{labelBadge(r.label)}</span>
                      {isCurrent && <Badge variant="outline" className="ml-1.5 rounded-sm text-[10px] border-cyan-300 bg-cyan-50 text-cyan-800">CURRENT</Badge>}
                    </div>
                    <span className="font-mono text-sm font-bold">{fmt(r.sale_price)}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    {r.label === "accepted" && r.accepted_at
                      ? <>Accepted {fmtDateTime(r.accepted_at)}{r.signature_name ? <> by <b>{r.signature_name}</b></> : null}</>
                      : <>Captured {fmtDateTime(r.captured_at)}</>}
                  </div>
                  <div className="flex gap-3 mt-2">
                    <button className="text-[11px] font-bold text-cyan-700 hover:underline inline-flex items-center gap-1"
                      onClick={() => window.location.assign(`/quotes/v2/${quoteId}?rev=${r.revision}`)}
                      data-testid={`view-snapshot-${r.revision}`}>
                      <Eye size={11} /> View snapshot
                    </button>
                    {!isCurrent && (
                      <button className="text-[11px] font-bold text-cyan-700 hover:underline inline-flex items-center gap-1"
                        onClick={() => setCompareRev(r.revision)}
                        data-testid={`compare-rev-${r.revision}`}>
                        <GitCompareArrows size={11} /> Compare with current
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {!isLoading && revs.length === 0 && curHasSnapshot === false && null}
            {!isLoading && revs.length === 0 && (
              <div className="text-xs text-slate-400 py-4 text-center">No sent/accepted versions yet — a snapshot is frozen the first time this quote is sent.</div>
            )}
          </div>
        </SheetContent>
      </Sheet>
      <CompareRevisionsDialog quoteId={quoteId} rev={compareRev} open={!!compareRev} onOpenChange={(o) => !o && setCompareRev(null)} />
    </>
  );
}
